import {
  FaBell,
  FaClockRotateLeft,
  FaCreditCard,
  FaRotateRight,
  FaTriangleExclamation,
} from 'react-icons/fa6'
import apiClient from '../../../services/apiClient'

export function RiskPanel({ apiToken, expanded = false, onRefresh, onSectionChange, orderRows = [], consignmentRows = [] }) {
  const unpaidRows = orderRows.filter((row) => ['payment pending', 'penalty applied', 'relist queued'].includes(row.status))
  const penaltyRows = orderRows.filter((row) => row.status === 'penalty applied' || row.action === 'Reminder ready')
  const relistReadyRows = orderRows.filter((row) => ['payment pending', 'penalty applied'].includes(row.status) && row.relistAt !== 'Not eligible')
  const payoutRows = orderRows.filter((row) => row.status === 'penalty applied' || row.status === 'relist queued')
  const consignmentReviewRows = consignmentRows.filter((row) => ['submitted', 'photos needed', 'approved'].includes(row.status) || row.workflowStage === 'Ready for decision')
  const consignmentPayoutRows = consignmentRows.filter((row) => row.rawPayoutStatus === 'manual-pending')
  const consignmentPayoutExposure = consignmentPayoutRows.reduce((sum, row) => sum + (row.rawPayoutAmount || row.rawExpectedPayoutAmount || 0), 0)

  const sendEligibleReminders = async () => {
    if (!apiToken) return
    try {
      const response = await apiClient.sendEligibleOrderReminders(apiToken)
      await onRefresh?.(`${response.sent} eligible checkout reminders sent`)
    } catch (error) {
      await onRefresh?.(error.message || 'Eligible reminder job failed')
    }
  }

  const applyPenalties = async () => {
    if (!apiToken) return
    try {
      const response = await apiClient.applyOrderPenalties(apiToken)
      await onRefresh?.(`${response.applied} unpaid penalties applied`)
    } catch (error) {
      await onRefresh?.(error.message || 'Penalty job failed')
    }
  }

  const runLifecycle = async () => {
    if (!apiToken) return
    try {
      const response = await apiClient.runAuctionLifecycle(apiToken)
      const result = response.result
      await onRefresh?.(`Lifecycle complete: ${result.scheduledStarted} started, ${result.won} won, ${result.closedWithoutWinner} closed`)
    } catch (error) {
      await onRefresh?.(error.message || 'Auction lifecycle failed')
    }
  }

  const runOperations = async () => {
    if (!apiToken) return
    try {
      const response = await apiClient.runOperationsJobs(apiToken)
      const result = response.result

      if (result.skipped) {
        await onRefresh?.(`Operations job skipped: ${result.reason}`)
        return
      }

      await onRefresh?.(`Operations complete: ${result.checkoutRemindersSent} reminders, ${result.penaltiesApplied} penalties, ${result.relistEligibleCount} relist reviews`)
    } catch (error) {
      await onRefresh?.(error.message || 'Operations job failed')
    }
  }

  return (
    <section className={`tactical-card obc-admin__risk ${expanded ? 'is-expanded' : ''}`}>
      <div className="obc-admin__panel-head">
        <h2>Risk & Relist Queue</h2>
        <span>Unpaid win enforcement</span>
      </div>
      <strong>{unpaidRows.length}</strong>
      <p>{unpaidRows.length || consignmentReviewRows.length ? `${penaltyRows.length} order${penaltyRows.length === 1 ? '' : 's'} and ${consignmentReviewRows.length} consignment${consignmentReviewRows.length === 1 ? '' : 's'} need review, payout, or relist action.` : 'No unpaid or relist-risk orders or consignments are currently active.'}</p>
      {expanded ? (
        <div className="obc-admin__risk-breakdown">
          <article><strong>{penaltyRows.length}</strong><span>Reminder / penalty queue</span></article>
          <article><strong>{relistReadyRows.length}</strong><span>Relist eligible</span></article>
          <article><strong>{consignmentReviewRows.length}</strong><span>Consignment review</span></article>
          <article><strong>{formatCurrency(consignmentPayoutExposure)}</strong><span>Consignment payout exposure</span></article>
        </div>
      ) : null}
      <div className="obc-admin__risk-actions">
        <button type="button" onClick={runOperations}><FaClockRotateLeft /> Run Operations Jobs</button>
        <button type="button" onClick={sendEligibleReminders}><FaBell /> Send Reminders</button>
        <button type="button" onClick={applyPenalties}><FaTriangleExclamation /> Apply Penalties</button>
        <button type="button" onClick={() => onSectionChange('orders')}><FaRotateRight /> Relist Eligible</button>
        <button type="button" onClick={() => onSectionChange('consignments')}><FaCreditCard /> Review Consignments</button>
        <button type="button" onClick={runLifecycle}><FaClockRotateLeft /> Run Auction Jobs</button>
        <button type="button" onClick={() => onSectionChange('orders')}><FaCreditCard /> Review Orders</button>
      </div>
    </section>
  )
}

function formatCurrency(value) {
  return `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: value % 1 ? 2 : 0, minimumFractionDigits: value % 1 ? 2 : 0 })}`
}

export function AdminRiskModule({ apiToken, consignmentRows, onRefresh, onSectionChange, orderRows }) {
  return <RiskPanel apiToken={apiToken} consignmentRows={consignmentRows} expanded onRefresh={onRefresh} onSectionChange={onSectionChange} orderRows={orderRows} />
}
